import { useTranslation } from 'react-i18next';
import { typeDef } from '../../featureTypes/registry';
import { geometryStats, geometryAnchor } from '../../geo/measure';
import { num } from '../../geo/format';
import type { Geometry } from '../../geo/types';
import { syncStateOf, type FeatureRecord, type PropValue } from '../../lib/types';
import { isAdmin, useCurrentMap, useStore } from '../../store/store';
import { resolveSync } from '../../store/actions';

function geomSummary(g: Geometry | null): string {
  if (!g) return '—';
  const a = geometryAnchor(g);
  const s = geometryStats(g);
  const parts: string[] = [g.type];
  if (a) parts.push(`@ ${num(a.x, 1)}, ${num(a.z, 1)}`);
  if (s.area !== undefined) parts.push(`${num(s.area, 0)} m²`);
  if (s.vertices !== undefined) parts.push(`${s.vertices} pts`);
  if (g.type === 'circle') parts.push(`r ${num(g.r, 1)}`);
  return parts.join(' ');
}

function show(v: PropValue): string {
  if (v === undefined || v === null || v === '') return '—';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

export function UnityTab({ feature: f }: { feature: FeatureRecord }) {
  const { t } = useTranslation();
  const admin = isAdmin(useStore((s) => s.user));
  const map = useCurrentMap();
  const uo = useStore((s) => (f.unityKey ? s.unityObjects[f.unityKey] : undefined));
  const def = typeDef(f.type);
  const sync = syncStateOf(f, uo);

  if (!def.unity) return <p className="muted">{t('unity.designOnlyType')}</p>;
  const scene =
    def.unity.scene === 'asset' ? t('unity.asset') : (map?.unityScenes?.[def.unity.scene] ?? def.unity.scene);
  const idValue = def.unity.idField ? (f.properties[def.unity.idField] as string | undefined) : undefined;

  const propNames = def.props
    .filter((p) => !p.design && p.kind !== 'featureRef' && p.kind !== 'featureRefs')
    .map((p) => p.name);
  const diffs = uo
    ? [
        { field: t('common.name'), design: f.name, unity: uo.name },
        { field: t('unity.geometry'), design: geomSummary(f.geometry), unity: geomSummary(uo.geometry) },
        ...propNames.map((n) => ({
          field: t(`props.${n}`, { defaultValue: n }),
          design: show(f.properties[n]),
          unity: show(uo.properties?.[n]),
        })),
      ]
    : [];

  return (
    <div className="unity">
      <dl className="stats">
        <dt>{t('unity.component')}</dt>
        <dd className="mono">{def.unity.component}</dd>
        <dt>{t('unity.scene')}</dt>
        <dd className="mono small">{scene}</dd>
        {def.unity.idField && (
          <>
            <dt>{t('unity.idField', { field: def.unity.idField })}</dt>
            <dd className="mono">{idValue || '—'}</dd>
          </>
        )}
        <dt>{t('unity.link')}</dt>
        <dd>
          <span className={`sync-pill ${sync}`}>{t(`sync.${sync}`)}</span>
        </dd>
        {uo && (
          <>
            <dt>{t('unity.hierarchy')}</dt>
            <dd className="mono small">{uo.hierarchyPath}</dd>
            <dt>{t('unity.unityPosition')}</dt>
            <dd className="mono small">
              Y {num(uo.y)} · rotY {num(uo.rotY)}
            </dd>
          </>
        )}
      </dl>
      <p className="muted small">{t(`unity.explain.${sync}`)}</p>

      {uo && (
        <table className="diff small">
          <thead>
            <tr>
              <th />
              <th>{t('unity.design')}</th>
              <th>Unity</th>
            </tr>
          </thead>
          <tbody>
            {diffs.map((d) => (
              <tr key={d.field} className={d.design !== d.unity ? 'changed' : ''}>
                <td>{d.field}</td>
                <td>{d.design}</td>
                <td>{d.unity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {admin && f.unityKey && (
        <div className="row wrap">
          {uo && sync !== 'in_sync' && (
            <>
              <button onClick={() => void resolveSync(f.id, 'take_unity')}>{t('unity.takeUnity')}</button>
              <button onClick={() => void resolveSync(f.id, 'keep_design')}>{t('unity.keepDesign')}</button>
            </>
          )}
          <button
            className="danger"
            onClick={() => {
              if (confirm(t('unity.confirmUnlink'))) void resolveSync(f.id, 'unlink');
            }}
          >
            {t('unity.unlink')}
          </button>
        </div>
      )}
    </div>
  );
}
